import httpClient from '@/services/http-client';
import { serialize } from 'object-to-formdata';

class Api {
  // account
  public login(email: string, password: string, captchaKey: string, captchaCode: string) {
    return httpClient.api.post(`account/login`, {
      email,
      password,
      captchaKey,
      captchaCode
    });
  }

  public getCaptcha() {
    return httpClient.api.get(`account/captcha`, {
      responseType: "arraybuffer"
    });
  }

  public register(firstName: string, lastName: string, phoneNumber: string, email: string, password: string, referralCode: string) {
    return httpClient.api.post(`account/register`, {
      firstName,
      lastName,
      phoneNumber,
      email,
      password,
      referralCode
    });
  }

  public forgotPassword(email: string) {
    return httpClient.api.post(`account/forgot`, {
      email
    });
  }

  public confirmForgotPassword(confirmCode: string, password: string, email: string) {
    return httpClient.api.post('account/forgot-confirm', {
      confirmCode,
      password,
      email
    });
  }

  public logout() {
    return httpClient.api.post(`account/logout`);
  }

  // company
  public getCompanies() {
    return httpClient.api.get(`company/list`);
  }

  public getCompanyShortList() {
    return httpClient.api.get(`company/short-list`);
  }

  public getCompany() {
    return httpClient.api.get(`company/get`);
  }

  public createCompany(data: any) {
    return httpClient.api.post(`company/create`, data);
  }

  public editCompany(data: any) {
    return httpClient.api.put(`company/edit`, data);
  }

  public deletCompany(id: number) {
    return httpClient.api.delete(`company/delete/${id}`);
  }

  public enableCompany(id: number) {
    return httpClient.api.put(`company/enable/${id}`);
  }

  public disableCompany(id: number) {
    return httpClient.api.put(`company/disable/${id}`);
  }

  // group
  public getGroups(data?: any) {
    return httpClient.api.get(`group/list`, {
      params: data
    });
  }

  public getGroupShortList() {
    return httpClient.api.get(`group/short-list`);
  }

  public getGroup(id: number) {
    return httpClient.api.get(`group/get/${id}`);
  }

  public createGroup(data: any) {
    return httpClient.api.post(`group/create`, data);
  }

  public editGroup(data: any) {
    return httpClient.api.put(`group/edit`, data);
  }

  public deletGroup(id: number) {
    return httpClient.api.delete(`group/delete/${id}`);
  }

  public chargeGroup(id: number, amount: number) {
    return httpClient.api.post(`group/group-charge`, {
      id,
      amount
    });
  }

  public chargeGroupList(id: number, amount: number, phoneNumbers: string[]) {
    return httpClient.api.post(`group/group-charge-list`, {
      id,
      amount,
      phoneNumbers
    });
  }

  public packageGroup(id: number, amount: number) {
    return httpClient.api.post(`group/group-package`, {
      id,
      amount
    });
  }

  public packageGroupList(id: number, amount: number, phoneNumbers: string[]) {
    return httpClient.api.post(`group/group-package-list`, {
      id,
      amount,
      phoneNumbers
    });
  }

  public enableGroup(id: number) {
    return httpClient.api.put(`group/enable/${id}`);
  }

  public disableGroups(id: number) {
    return httpClient.api.put(`group/disable/${id}`);
  }

  public getGroupItems(data: any) {
    return httpClient.api.post(`group/item-list`, data);
  }

  public getGroupItemDownload(data: any) {
    return httpClient.api({
      url: 'group/item-list-download',
      method: 'post',
      data: data,
      responseType: 'blob'
    });
  }

  public createGroupItem(id: number, sim: string) {
    return httpClient.api.post(`group/item-create`, {
      id,
      sim
    });
  }

  public deleteGroupItem(id: number) {
    return httpClient.api.delete(`group/item-delete/${id}`);
  }

  // group wallet
  public getGroupWallets(groupId: number) {
    return httpClient.api.get(`group-wallet/list/${groupId}`);
  }

  // group Wallet transfer
  public getGroupWalletDeposits(groupId: number) {
    return httpClient.api.get(`group-wallet-transfer/deposit-list/${groupId}`);
  }

  public createGroupWalletDeposit(data: any) {
    return httpClient.api.post(`group-wallet-transfer/create-deposit`, data);
  }

  // invoice
  public getInvoices() {
    return httpClient.api.get(`invoice/list`);
  }

  public getInvoice(id: number) {
    return httpClient.api.get(`invoice/get/${id}`);
  }

  public cancelInvoice(id: number) {
    return httpClient.api.put(`invoice/cancel/${id}`);
  }

  // order
  public payOrder(id: number) {
    return httpClient.api.post(`order/pay/${id}`);
  }

  //////////////////// PORTAL
  // notification
  public getNotifications() {
    return httpClient.api.get(`notification/list`);
  }

  public getNotificationShortList() {
    return httpClient.api.get(`notification/short-list`);
  }

  // product
  public getProducts(id: number) {
    return httpClient.api.get(`product/list/${id}`);
  }

  // business unit
  public getBusinessUnits() {
    return httpClient.api.get(`business-unit/list`);
  }

  // product category
  public getProductCategories() {
    return httpClient.api.get(`product-category/list`);
  }

  // profile
  public getUserProfile() {
    return httpClient.api.get(`profile/get`);
  }

  public getUserNotificationStatus() {
    return httpClient.api.post(`profile/get-notification-status`);
  }

  public editProfile(firstName: string, lastName: string) {
    return httpClient.api.put('profile/edit', {
      firstName,
      lastName
    });
  }

  public editAccountType(isCompanyAccount: boolean) {
    return httpClient.api.put(`profile/edit-account-type`, {
      isCompanyAccount
    });
  }

  public editPhoneNumber(phoneNumber: string) {
    return httpClient.api.put(`profile/edit-phone-number`, {
      phoneNumber
    });
  }

  public confirmPhoneNumber(confirmCode: string) {
    return httpClient.api.post(`profile/confirm-phone-number`, {
      confirmCode
    });
  }

  public editEmail(email: string) {
    return httpClient.api.put(`profile/edit-email`, {
      email
    });
  }

  public confirmEmail(confirmCode: string) {
    return httpClient.api.post(`profile/confirm-email`, {
      confirmCode
    });
  }

  public editPhoneNotification(phoneNotify: boolean) {
    return httpClient.api.put(`profile/edit-phone-notification`, {
      phoneNotify
    });
  }

  public editEmailNotification(emailNotify: boolean) {
    return httpClient.api.put(`profile/edit-email-notification`, {
      emailNotify
    });
  }

  public changePassword(oldPassword: string, newPassword: string, confirmPassword: string) {
    return httpClient.api.post(`profile/change-password`, {
      oldPassword,
      newPassword,
      confirmPassword
    });
  }

  // sim
  public getSims(data: any) {
    return httpClient.api.post(`sim/list`, data);
  }

  public importSims(companyId: number, groupId: number, simList: any) {
    return httpClient.api.post(
      'sim/import',
      serialize({
        companyId,
        groupId,
        simList
      })
    );
  }

  public importOneSims(companyId: number, groupId: number, phoneNumber: string) {
    return httpClient.api.post(`sim/import-one`, {
      companyId,
      groupId,
      phoneNumber
    });
  }

  public editSim(data: any) {
    return httpClient.api.put(`sim/edit`, data);
  }

  public enableSim(id: number) {
    return httpClient.api.put(`sim/enable/${id}`);
  }

  public disableSim(id: number) {
    return httpClient.api.put(`sim/disable/${id}`);
  }

  public updateCredit(id: number) {
    return httpClient.api.put(`sim/update-credit/${id}`);
  }

  public simCharge(phoneNumber: string, groupId: number, amount: number) {
    return httpClient.api.post(`sim/sim-charge`, {
      phoneNumber,
      groupId,
      amount
    });
  }

  public simPackage(phoneNumber: string, groupId: number, productId: number) {
    return httpClient.api.post(`sim/sim-package`, {
      phoneNumber,
      groupId,
      productId
    });
  }

  // Support
  public getSupports() {
    return httpClient.api.get(`support/list`);
  }

  public createSupport(content: string, businessUnitId: number, supportSubjectId: number, productCategoryId: number) {
    return httpClient.api.post(`support/create`, {
      content,
      businessUnitId,
      supportSubjectId,
      productCategoryId
    });
  }

  // Support Item
  public getSupportItems(id: number) {
    return httpClient.api.get(`support-item/list/${id}`);
  }

  public createSupportItem(supportId: number, content: string) {
    return httpClient.api.post(`support-item/create`, {
      supportId,
      content
    });
  }

  // Support subject
  public getSupportSubjects() {
    return httpClient.api.get(`support-subject/list`);
  }

  public getSelectSupportSubjects(productCategoryId: number, businessUnitId: number) {
    return httpClient.api.post(`support-subject/select-list`, {
      productCategoryId,
      businessUnitId
    });
  }

  public createSupportSubject(data: any) {
    return httpClient.api.post(`support-subject/create`, data);
  }

  public editSupportSubject(data: any) {
    return httpClient.api.put(`support-subject/edit`, data);
  }

  public deleteSupportSubject(id: number) {
    return httpClient.api.delete(`support-subject/delete/${id}`);
  }

  // user
  public getUsers() {
    return httpClient.api.get(`user/list`);
  }

  public getUserShortList() {
    return httpClient.api.get(`user/short-list`);
  }

  public getUser(id: number) {
    return httpClient.api.get(`user/get/${id}`);
  }

  public createUser(data: any) {
    return httpClient.api.post(`user/create`, data);
  }

  public editUser(data: any) {
    return httpClient.api.put(`user/edit`, data);
  }

  public deleteUser(id: number) {
    return httpClient.api.delete(`user/delete/${id}`);
  }

  public resetPassword(data: any) {
    return httpClient.api.put(`user/reset-password`, data);
  }

  public disableUser(id: number) {
    return httpClient.api.put(`user/disable/${id}`);
  }

  public enableUser(id: number) {
    return httpClient.api.put(`user/enable/${id}`);
  }

  // Role
  public getRoles() {
    return httpClient.api.get(`role/list`);
  }

  // payment
  public getWalletPayments() {
    return httpClient.api.get(`wallet-payment/list`);
  }

  public getWalletPayment(id: number) {
    return httpClient.api.get(`wallet-payment/get/${id}`);
  }

  public createWalletPayment(paymentProviderId: number, amount: number) {
    return httpClient.api.post(`wallet-payment/create`, {
      paymentProviderId,
      amount
    });
  }

  // wallet
  public getWalletTransactions(companyId: number) {
    return httpClient.api.get(`wallet/list/${companyId}`);
  }

  public getWalletBalances() {
    return httpClient.api.get(`wallet/balance-list`);
  }

  public getWalletBalance() {
    return httpClient.api.get(`wallet/get-balance`);
  }

  // Wallet bank-transfer
  public getWalletDeposits(companyId: number) {
    return httpClient.api.get(`wallet-transfer/deposit-list/${companyId}`);
  }

  public createWalletDeposit(data: any) {
    return httpClient.api.post(`wallet-transfer/create-deposit`, data);
  }

  public getWalletWithdraws(companyId: number) {
    return httpClient.api.get(`wallet-transfer/withdraw-list/${companyId}`);
  }

  public createWalletWithdraw(data: any) {
    return httpClient.api.post(`wallet-transfer/create-withdraw`, data);
  }

  // Report
  public getDashboard(data: any) {
    return httpClient.api.post(`report/get-dashboard`, data);
  }

  public getReportSim(data: any) {
    return httpClient.api.post(`report/report-sim`, data);
  }

  public getGroupRuns(params: { groupId?: number; outcome?: number | null; take?: number }) {
    return httpClient.api.get('/group/runs', { params });
  }
}
export default new Api();
